import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import matchingRoundsAPI, {
  MatchingRound,
} from '../../api/matching-rounds';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { Button, ConfirmDialog, SnackMessage } from '../common';
import { ActionsDialog } from '../common/Confirmation/ActionsDialog';
import TableActions from '../common/table-actions';

function MatchingRoundsRowActions({ round }: { round: MatchingRound }) {
  const { hasAccessToAction } = useContext(UserContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();

  const [isReviewConfirmOpen, setIsReviewConfirmOpen] = useState(false);
  const [isReviewSuccessOpen, setIsReviewSuccessOpen] = useState(false);
  const [isSendingReview, setIsSendingReview] = useState(false);

  const handleConfirmReview = useCallback(async () => {
    setIsReviewConfirmOpen(false);
    try {
      setIsSendingReview(true);
      await matchingRoundsAPI.sendApprovals(round.id);
      setIsReviewSuccessOpen(true);
    } catch (e: unknown) {
      const messageError = (e as AxiosError)?.response?.data?.message;
      enqueueSnackbar(
        'An error occurred while sending approval requests.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
    } finally {
      setIsSendingReview(false);
    }
  }, [round.id, enqueueSnackbar]);

  return (
    <>
      <TableActions
        actions={[
          {
            key: 'row-action-subgroups',
            onClick: () =>
              history.push(
                getRoutePath(Pages.MATCHING_ROUND_SUBGROUPS, {
                  roundId: round.id,
                }),
              ),
            label: 'Subgroups',
            hidden: !hasAccessToAction('matchingRound.details.view'),
          },
          {
            key: 'row-action-heatmap',
            onClick: () =>
              history.push(
                getRoutePath(Pages.MATCHING_ROUND_HEATMAP, {
                  roundId: round.id,
                }),
              ),
            label: 'Heatmap',
            hidden: !hasAccessToAction('matchingRound.details.view'),
          },
          {
            key: 'row-action-send-review',
            onClick: () => setIsReviewConfirmOpen(true),
            label: 'Send for Review',
            hidden: !hasAccessToAction('matchingRound.details.update'),
            disabled: isSendingReview,
          },
        ]}
      />
      <ConfirmDialog
        isOpen={isReviewConfirmOpen}
        title='Send for Review'
        body={`This action will send emails to all Ventures and ${rb(
          'mentors-u',
        )} in this round to vote on proposed matches. Do you agree?`}
        onCancel={() => setIsReviewConfirmOpen(false)}
        onSuccess={handleConfirmReview}
        cancelProps={{ label: 'No', variant: 'outlined' }}
        successProps={{
          label: 'Yes',
          variant: 'contained',
          'data-testid': 'confirm-review-yes',
        }}
      />
      <ActionsDialog
        open={isReviewSuccessOpen}
        onClose={() => setIsReviewSuccessOpen(false)}
        title='Review Requested'
        actions={
          <Button
            onClick={() => setIsReviewSuccessOpen(false)}
            variant='contained'
            data-testid='confirm-review-ok'
          >
            Ok
          </Button>
        }
      >
        Emails will be sent out momentarily
      </ActionsDialog>
    </>
  );
}

export default MatchingRoundsRowActions;
